import { z } from 'zod';
import { VisitClosedReasonSchema, VisitPositionReviewSchema } from './vocabulary.js';
const Id16 = z.string().max(16);
/** A visit — one person's presence episode at one location. The position is the
 *  check-in ceremony's frozen verdict (a same-site re-check-in refreshes it,
 *  audited); null = no fix shared. */
export const VisitSchema = z
    .object({
    id: Id16,
    site_id: Id16,
    checked_in_at: z.string(),
    checked_out_at: z.string().nullable(),
    closed_reason: VisitClosedReasonSchema.nullable(),
    position: z.object({ match: z.boolean(), distance_m: z.number().int().nullable() }).strict().nullable(),
    /** What the position MEANS for captures under this visit, under the
     *  project's review policy now — the consequence a channel tells the
     *  person; null = nothing waits on the position's account. */
    position_review: VisitPositionReviewSchema.nullable(),
})
    .strict();
/** Check-in's three ruled outcomes, named so the bot can speak them. */
export const CheckInResponseSchema = z
    .object({ visit: VisitSchema, outcome: z.enum(['opened', 'refreshed', 'superseded_previous']) })
    .strict();
export const VisitsListResponseSchema = z.object({ items: z.array(VisitSchema) }).strict();
/** The 202 answer to a retry racing a still-running ingest of the same capture. */
export const CapturePendingResponseSchema = z.object({ status: z.literal('pending'), file_id: Id16 }).strict();
// ── the hub — the ticket on the wire (ADR-073 §7) ────────────────────────────
// A REFERENCE to a visit, never a copy of its facts: the visit's own times and
// frozen verdict ride the visit object (read through the ticket's visit line);
// nothing positional or temporal about a visit is stored on the ticket.
import { paginatedResponseSchema } from '../common/pagination.js';
import { BandKeySchema, BandSchema } from '../common/bands.js';
import { CaptureStageSchema, LifecycleEventSchema, TicketClassSchema } from './vocabulary.js';
export const TicketPersonSchema = z.object({ id: Id16, label: z.string() }).strict();
/** Where the ticket sits — a site, a depot, or the project itself (never a multi-anchor row). */
export const TicketLocationSchema = z
    .object({
    kind: z.enum(['site', 'warehouse', 'project']),
    id: Id16.nullable(),
    name: z.string(),
    /** A site's reference (the copiable code); null for a depot or the project. */
    reference: z.string().nullable(),
    /** The site's status — a LABEL on the row (the hub is a record surface); null off a site. */
    status: z.string().nullable(),
    /** The site's zone — the Visit cell reads the visit in SITE time (ADR-082 §2); null off a site
     *  (a depot's or the project's ticket then reads in the screen's zone). */
    timezone_iana: z.string().nullable(),
    /** May THIS viewer open the site (its peek and page) — the site read's own rule: the sites key and the site in
     *  the viewer's reach. A participant (the ticket's creator) reaches their ticket at a site outside their reach:
     *  the name then reads as plain text, never a link onto a refusal (ADR-036 §6). False off a site. */
    can_open: z.boolean(),
})
    .strict();
/** A visit as the ticket lists it — the visit RECORD (the ceremony facts + the
 *  evidence qualifier), read through the ticket's visit line. */
export const TicketVisitSchema = z
    .object({
    id: Id16,
    person: TicketPersonSchema.nullable(),
    checked_in_at: z.string(),
    checked_out_at: z.string().nullable(),
    closed_reason: VisitClosedReasonSchema.nullable(),
    /** The frozen check-in verdict; null = no fix shared. */
    position: z.object({ match: z.boolean(), distance_m: z.number().int().nullable() }).strict().nullable(),
})
    .strict();
/** The last lifecycle line — the Status detail line's source ("Closed — 7-day
 *  grace" · "Reviewed by Nadia 16:30"): the cause, who (null = the platform),
 *  and the two clocks. */
export const TicketLifecycleSchema = z
    .object({
    event: LifecycleEventSchema,
    stage_to: CaptureStageSchema,
    author: TicketPersonSchema.nullable(),
    at: z.string(),
    effective_at: z.string().nullable(),
})
    .strict();
/** A subject outside the viewer's reach — its KIND alone, "a unit"; its name
 *  and its id never travel (the naming rule). */
export const TicketHiddenSubjectSchema = z.object({ hidden: z.literal(true), kind: z.literal('unit') }).strict();
/** The What column — what the photos show: the confirmed subjects and the
 *  topics across the ticket's files, capped (the client prints the words). */
export const TicketWhatSchema = z
    .object({
    subjects: z.array(z.union([z.object({ id: Id16, label: z.string(), category: z.string().nullable() }).strict(), TicketHiddenSubjectSchema])).max(3),
    topics: z.array(z.string().max(32)).max(3),
    /** How many more subjects + topics exist past the cap. */
    more: z.number().int().nonnegative(),
})
    .strict();
export const TicketListItemSchema = z
    .object({
    id: Id16,
    number: z.number().int().positive(),
    class: TicketClassSchema,
    type: z.string().max(32),
    stage: CaptureStageSchema,
    /** Photos awaiting a review answer — non-zero on to_review (kept live after close). */
    waiting_count: z.number().int().nonnegative(),
    /** The ticket's files, both natures. */
    evidence_count: z.number().int().nonnegative(),
    location: TicketLocationSchema,
    /** The DOER — a v1 capture's technician (the creator). */
    person: TicketPersonSchema.nullable(),
    /** ONE visit for the cell: an OPEN one beats the latest closed (the pick-order). */
    visit: TicketVisitSchema.nullable(),
    visits_count: z.number().int().nonnegative(),
    what: TicketWhatSchema,
    created_at: z.string(),
    completed_at: z.string().nullable(),
    closed_at: z.string().nullable(),
    closed_by: TicketPersonSchema.nullable(),
    reopen_count: z.number().int().nonnegative(),
    last_activity_at: z.string(),
    /** When the grace files it (completed_at + the project's grace); null = never, or not completed. */
    grace_closes_at: z.string().nullable(),
    dormant: z.boolean(),
    followed: z.boolean(),
    last_event: TicketLifecycleSchema.nullable(),
})
    .strict();
/** A row of the LIST: the ticket, and — in a grouped answer — the key of the band it sits in, stamped by the server
 *  (the same expression that counted the bands; the client never computes one — ADR-078 §4). */
export const TicketListRowSchema = TicketListItemSchema.extend({ band: BandKeySchema.optional() }).strict();
/** The strip's truthful counts (ADR-078 §2) — the three open stages and the
 *  facts, counted in ONE pass over the OPEN work, each EXEMPTING its own axis
 *  (a chip's number holds still when you press it); the stages partition, the
 *  facts overlap. `closed` is the filed rows inside the Closed WINDOW, counted
 *  CAPPED (`HISTORY_COUNT_CAP + 1` = "1,000+"); null while the window is hidden. */
export const TicketStageCountsSchema = z
    .object({ in_progress: z.number().int(), to_review: z.number().int(), completed: z.number().int(), closed: z.number().int().nullable() })
    .strict();
export const TicketFactCountsSchema = z.object({ dormant: z.number().int(), checked_in_now: z.number().int(), followed: z.number().int() }).strict();
/** The list's answer. Grouped (`group_by`): `bands[]` — every band of the filtered population with its TRUE count, in
 *  the axis's order (recency newest first, reversed by an ascending time sort — activity or created; names A→Z, "not set" last), the
 *  count a FLOOR (`capped`) where the Closed window's one capped walk ended before it — and `items` = the STREAM: the
 *  OPENED bands' rows in band order then the sort, paged; `total` = the opened bands' counts summed (0 when nothing is
 *  open — the cheapest answer, the overview). Ungrouped: no `bands`, the plain page. */
export const TicketsListResponseSchema = paginatedResponseSchema(TicketListRowSchema).extend({
    stage_counts: TicketStageCountsSchema,
    fact_counts: TicketFactCountsSchema,
    /** The project's capture grace in days (null = never) — the Status line's "Closed — 7-day grace". */
    grace_days: z.number().int().nullable(),
    bands: z.array(BandSchema).optional(),
    /** With `bands`: the walk's REACH when the Closed window ran past the cap — the instant the counts cover the filed
     *  rows back to (the cap row's activity): the stream shows no filed row before it; a ladder lists the rungs past it
     *  as floors of nothing ("0+"); a data-keyed axis may be missing a band whose rows all lie before it. null = the
     *  window was counted whole. The screen says it in one line and names the Closed window as the way to older rows. */
    bands_from: z.string().nullable().optional(),
});
export const TicketDetailSchema = TicketListItemSchema.extend({ visits: z.array(TicketVisitSchema), grace_days: z.number().int().nullable() }).strict();
/** Why ONE close is refused — the single act's named doors (the batch selects
 *  Completed rows alone, so it refuses nothing). */
export const TICKET_CLOSE_REFUSALS = ['PHOTOS_AWAIT_REVIEW', 'VISIT_OPEN', 'ALREADY_CLOSED'];
export const TicketCloseRefusalSchema = z.enum(TICKET_CLOSE_REFUSALS);
/** The batch's honest count: how many were filed, and how many Completed rows
 *  the same filters still hold past the act's cap ("1,000 closed, 240 left"). */
export const TicketsCloseResponseSchema = z.object({ closed: z.number().int().nonnegative(), remaining: z.number().int().nonnegative() }).strict();
export const FollowStateSchema = z.object({ followed: z.boolean() }).strict();
